import * as React from "react";
import { useDispatch } from "redux-react-hook";
import { toast } from "react-toastify";
import styled from "styled-components";

import { FilterParams } from "src/library/utils";
import { ActionButton } from "src/library/components/Button";
import Table, { TableRefProps } from "src/core/dashboard/components/widgets/table/Table";
import {
  IDrop,
  useDropsApi
} from "src/core/drops/api/drops.api";
import { setDrop } from "src/core/drops/store/drops.actions";
import { PaginatedParams } from "src/core/shared/api";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { getDropTableColumns } from "src/core/shared/components/tables/dropsTableColumns";
import { PageProps } from "src/pages/Router/routes";
import { DeactivateDropModal } from "src/core/drops/components/DeactivateDrop";
import { useDropActions } from "src/core/drops/hooks/useDropActions";

const DropsPageWrapper = styled.div``;

const defaultPagination: PaginatedParams<IDrop> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "name", "ASC" ] ]
};

/**
 * Main "Drops" View
 *
 * @returns
 */
const DropsPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const { history, terminology } = props;
  const dispatch = useDispatch();
  const TableRef = React.useRef<TableRefProps>(null);
  const [ dropsResponse, getDrops ] = useDropsApi("LIST_DROPS");
  const [ updateDropStatusResponse, updateDropStatus ] = useDropsApi("UPDATE_DROP_STATUS");
  const dropsData = dropsResponse.data;

  const {
    getDropActions, isLoading, deactivateModalState, setDeactivateModalState
  } = useDropActions();

  React.useEffect(() => {
    dispatch(setDrop(null));
  }, [ dispatch ]);

  /**
   * Handle update drop status api response
   */
  React.useEffect(() => {
    if (updateDropStatusResponse.data && TableRef.current) {
      TableRef.current.reloadTable();
      toast.success(updateDropStatusResponse.data.message);
    } else if (updateDropStatusResponse.error) {
      toast.error(updateDropStatusResponse.error.message);
    }
  }, [ updateDropStatusResponse.data, updateDropStatusResponse.error ]);

  /**
   * Fetches data from the api but removes any drops that are deactivated
   * unless otherwise requested
   */
  const onFetchData = React.useCallback(
    (params: {filters: FilterParams<IDrop>, pagination: PaginatedParams<IDrop>}) => {
      getDrops({
        pagination: params.pagination,
        filters: params.filters
      });
    },
    [ getDrops ]
  );

  const columns = React.useMemo(() => {
    return getDropTableColumns({
      actionButton: info => {
        return (
          <ActionButton
            key={`action-${info.row.original.id}`}
            title="Actions"
            items={getDropActions(info.row.original)}
            loading={isLoading}
          />
        );
      },
      terminology

    });
  }, [
    terminology,
    getDropActions,
    isLoading
  ]);

  return (
    <Layout {...props}>
      <MainContentWrapper fill>
        <DropsPageWrapper>
          <Table
            ref={TableRef}
            error={dropsResponse.error}
            tableOf={terminology.drop.plural.text}
            loading={dropsResponse.pending}
            data={dropsData ? dropsData.result : []}
            onFetchData={onFetchData}
            rowCount={dropsData ? dropsData.count : 0}
            pageCount={dropsData ? dropsData.pages : 0}
            defaultPagination={defaultPagination}
            mainAction={row => history.push(`/admin/customers/${row.original.customerId}/drops/${row.original.id}/orders`)}
            columns={columns}
          />
        </DropsPageWrapper>
      </MainContentWrapper>

      <DeactivateDropModal
        dropToDeactivate={deactivateModalState}
        onDismiss={() => setDeactivateModalState(null)}
        onSave={params => updateDropStatus(params)}
      />
    </Layout>
  );
};

export default DropsPage;
